import {
  Box,
  Button,
  CardRadioItem,
  CardRadioRoot,
  Icon,
  IconBackground,
  IconButton,
  Typography,
} from '@branch-messenger/willow-ui'
import {
  ChevronRight,
  CreditCard02,
  Pencil01,
  Plus,
} from '@branch-messenger/willow-ui/icons'
import { FC, useContext } from 'react'
import { Control, Controller } from 'react-hook-form'

import { iconMap } from '@/containers/connectedDebitCards/ConnectedCard'
import { CardSelectionSchema } from '@/containers/onDemandPay/TakeAdvance/lib/schemas/card-selection-schema'
import { useTakeAdvance } from '@/containers/onDemandPay/TakeAdvance/TakeAdvanceContext'
import { DebitCardModalContent } from '@/containers/payouts/DebitCardModalContent'
import { DialogContext } from '@/context'
import { useFormattedOrgFees } from '@/selectors/organization'
import { ConnectedCardSelection } from '@/types/card'
import { PayoutFeeType } from '@/types/organization'

import { AdvanceCardSelection } from '../../styles'

type ConnectDestinationCardProps = {
  cards: ConnectedCardSelection[]
  control: Control<CardSelectionSchema>
  selectedCardType: CardSelectionSchema['card_type']
}

export const SelectDestinationCard: FC<ConnectDestinationCardProps> = ({
  cards,
  control,
  selectedCardType,
}) => {
  const { setModalOpen } = useTakeAdvance()
  const { showAlert, showDialog } = useContext(DialogContext)

  const { data: fees } = useFormattedOrgFees(PayoutFeeType.EWA_DIRECT)
  const feeDescription = `Advance for a ${fees?.feePercentage} ${fees?.baseFee} fee to your debit card.`

  const handleDebitCardFormClose = () => {
    setModalOpen(true)
  }

  const handleDebitCardFormSuccess = () => {
    setModalOpen(true)
  }

  const showDebitCardForm = (connectCard?: boolean) => {
    showDialog({
      children: (
        <DebitCardModalContent
          type={connectCard ? 'EWA_CARD' : selectedCardType}
          onDebitCardFormClosed={handleDebitCardFormClose}
          onDebitCardFormSuccess={handleDebitCardFormSuccess}
        />
      ),
      hideCloseButton: true,
    })
  }
  const connectCard = () => {
    setModalOpen(false)
    showDebitCardForm(true)
  }

  const updateCard = () => {
    setModalOpen(false)
    showAlert({
      title: 'Are you sure?',
      description:
        'Updating your connected debit card will delete your current debit card information from your account.',
      actionDescription: 'Yes, Continue',
      cancelDescription: 'No, cancel',
      handleCancel() {
        handleDebitCardFormClose()
      },
      handleAction() {
        showDebitCardForm()
      },
    })
  }

  if (cards.length === 0) {
    return (
      <AdvanceCardSelection onClick={connectCard}>
        <IconBackground>
          <Icon Icon={CreditCard02} size={6} />
        </IconBackground>
        <Box>
          <Typography $bold $size="sm">
            Connect a debit card
          </Typography>
          <Typography $size="xs">{feeDescription}</Typography>
        </Box>
        <IconButton>
          <Icon Icon={ChevronRight} size={6} />
        </IconButton>
      </AdvanceCardSelection>
    )
  }

  return (
    <Box
      $display="flex"
      $direction="column"
      $align="initial"
      $gap={4}
      $fullWidth
    >
      <Controller
        name="card_type"
        control={control}
        render={({ field }) => (
          <CardRadioRoot {...field} onValueChange={field.onChange}>
            {cards.map(card => {
              const iconProps = iconMap[card.network.toLowerCase()]
              return (
                <CardRadioItem
                  key={card.id}
                  {...iconProps}
                  title={`${card.network} debit • ${card.last4}`}
                  subtitle={feeDescription}
                  value={card.type}
                  indicatorPosition="right"
                  SubIcon={Pencil01}
                  showIndicatorWithIcon={true}
                  color="weak"
                />
              )
            })}
          </CardRadioRoot>
        )}
      />
      <Box>
        <Button variant="link" iconLeft={<Plus />} onClick={updateCard}>
          Update Connected Card
        </Button>
      </Box>
    </Box>
  )
}
